// Lightweight inline disclosure (ghost-button trigger, no card chrome) for an
// optional/advanced group nested inside other content. To collapse a whole
// bordered Card from its own header, use `CardSection` with `collapsible` instead.
import { ReactNode, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import { Button } from '../../../components/ui/button';

interface CollapsibleSectionProps {
  /** Section label shown in the trigger */
  label: string;
  /** Summary shown inline when collapsed (string or ReactNode) */
  summary?: ReactNode;
  /** Content to show when expanded */
  children: ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Start open by default (uncontrolled mode) */
  defaultOpen?: boolean;
  /** Additional className on the root */
  className?: string;
  /** Additional className on the content wrapper */
  contentClassName?: string;
}

export function CollapsibleSection({
  label,
  summary,
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  className,
  contentClassName,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange} className={className}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground gap-1 px-1 h-auto py-1"
        >
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 transition-transform duration-200',
              isOpen && 'rotate-90'
            )}
          />
          <span>{label}</span>
          {!isOpen && summary && (
            <span className="ml-1 text-xs font-normal text-muted-foreground/70 truncate max-w-[300px]">
              {summary}
            </span>
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn('space-y-3 pt-2', contentClassName)}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
